import {h, Fragment} from "preact"
// buildShopifyMedia now expects an image data object (from Liquid) or null/undefined
const buildShopifyMedia = (imageDataSource, options = {}) => {
  const { alt: optionAlt, preview, width: optionWidth, height: optionHeight } = options;

  if (preview) {
    // In preview mode, always return a generic image placeholder
    return {
      src: 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_medium.png',
      alt: `Preview of ${optionAlt || imageDataSource?.alt || 'media'}`, // Use alt from imageDataSource if available
      type: 'image',
      width: optionWidth || 300,
      height: optionHeight || 200,
    };
  }

  // Check if imageDataSource is valid and has a 'url' property (which is what our Liquid now provides)
  if (!imageDataSource || typeof imageDataSource.url !== 'string' || !imageDataSource.url) {
    // If not, return a default placeholder
    return {
      src: 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png',
      alt: optionAlt || 'Placeholder media',
      type: 'image',
      width: optionWidth || 600,
      height: optionHeight || 400,
    };
  }

  // If imageDataSource is the already structured object from Liquid, use its properties
  return {
    src: imageDataSource.url, // This is the main image URL from Liquid's "url" field
    alt: imageDataSource.alt || optionAlt || 'Shopify Image', // Prioritize alt from imageDataSource
    type: 'image', // We assume 'image' because Liquid logic already filtered for media_type == 'image'
    width: imageDataSource.width || optionWidth,
    height: imageDataSource.height || optionHeight,
    // Pass through other specific URLs if your component logic needs them
    url_800: imageDataSource.url_800,
    url_400: imageDataSource.url_400,
  };
};

// Helper for text color classes (simplified from Liquid's `render '@text-color-class'`)
const getTextColorClass = (colorValue) => {
  if (!colorValue) return '';
  return `text-${colorValue}`;
};

// Helper for CTA button style classes
const getCtaButtonVariantClass = (styleHandle) => {
  return `btn-variant-${styleHandle}`;
};

const CollectionIngridItem = ({ item: initialItem, index, preview }) => {
  // 1. Resolve the actual item data
  // Handles the Liquid logic: if item.in_grid_content_item, assign item = item.in_grid_content_item.value
  const currentItem = initialItem?.in_grid_content_item?.value || initialItem;

  if (!currentItem) {
    return null; // Or render a placeholder
  }

  // Renamed variables for clarity - they now hold objects, not GIDs
  const {
    title,
    short_description: content,
    cta_text: ctaLabel,
    cta_url: link,
    desktop_image: desktopImageObject, // Changed from desktopImageGid
    mobile_image: mobileImageObject,   // Changed from mobileImageGid
    text_color: textColorValue,        // Value for text color  
    mobile_position: mobilePosition,
    desktop_position: desktopPosition,
    mobile_maximum_height: mobileMaximumHeight,
    desktop_maximum_height: desktopMaximumHeight
  } = currentItem;

  // Handle fields that might be nested under '.value' in the metaobject field definition
  const ctaStyleHandle = currentItem.cta_style?.value || 'secondary';
  const textPositionHandle = currentItem.text_position?.value || 'start';
  const swapDesktopColumns = currentItem.swap_desktop_columns?.value || false;

  // 3. Determine loading strategy
  const isLoadingEager = index < 3; // Eager load if it's one of the first few items

  // 4. Media Handling (Responsive Solution)
  // primaryImageSource now holds the image object from Liquid
  let primaryImageSource = desktopImageObject || mobileImageObject;
  
  const mediaData = buildShopifyMedia(primaryImageSource, { alt: title, preview: preview });

  // 5. Construct CSS classes
  const textColorClassName = getTextColorClass(textColorValue);

  const textboxConditionalClasses = [
    textPositionHandle === 'start' && 'place-items-start text-start',
    textPositionHandle === 'center' && 'place-items-center text-center',
    textPositionHandle === 'end' && 'place-items-end text-end',
    swapDesktopColumns && 'lg:order-first', // Handles desktop column swap
  ].filter(Boolean).join(' ');
  
  const textboxBaseClasses = 'relative z-10 p-sm pb-md lg:px-lg lg:grow grid grid-cols-1 gap-y-sm';
  const textboxClassName = `${textboxBaseClasses} ${textboxConditionalClasses}`;

  // 6. Media height and object-fit logic
  const shouldConstrainHeight = (mobileMaximumHeight > 0 || desktopMaximumHeight > 0);
  const objectFitClass = shouldConstrainHeight ? 'object-contain' : 'object-cover';

  // Base classes for the media container. The Liquid snippet used lg:w-7/12.
  const mediaContainerBaseClasses = shouldConstrainHeight ? 'w-full' : 'w-full lg:flex-none lg:w-7/12';
  
  // Create dynamic CSS classes for responsive height constraints
  const getHeightClasses = () => {
    const classes = [];
    if (mobileMaximumHeight > 0) {
      classes.push(`max-h-[${mobileMaximumHeight}px]`);
    }
    if (desktopMaximumHeight > 0) {
      classes.push(`lg:max-h-[${desktopMaximumHeight}px]`);
    }
    return classes.join(' ');
  };
  
  const heightConstraintClasses = getHeightClasses();

  return (
    <div className={`col-span-full row-start-${mobilePosition} lg:row-start-${desktopPosition}`}> {/* This outer div is now always rendered */}
      <div className={`items-center grid lg:flex ${textColorClassName}`}>
        {/* Media Element Section */}
        <div
          className={`${mediaContainerBaseClasses} ${
            mediaData.type === 'video' && !preview ? 'aspect-video' : '' // Video aspect ratio only if not in preview mode (where it becomes an image)
          }`}
        >
          {mediaData.src ? (
            <>
              {mediaData.type === 'image' ? (
                <img
                  src={mediaData.src}
                  alt={mediaData.alt || title || 'Ingrid item image'}
                  loading={isLoadingEager ? 'eager' : 'lazy'}
                  className={`w-full h-auto ${objectFitClass} ${heightConstraintClasses}`}
                  // For truly performant responsive images, `srcset` and `sizes` attributes
                  // should be generated by `buildShopifyMedia` or passed as props.
                  // Example from Liquid: sizes='732, (min-width: 1100px) 1464px'
                />
              ) : mediaData.type === 'video' ? (
                <video
                  src={mediaData.src}
                  className={`w-full h-full ${objectFitClass} ${heightConstraintClasses}`}
                  autoPlay
                  loop
                  muted
                  playsInline // Important for autoplay on mobile
                  controls={false}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <p>Unsupported media type: {mediaData.type}</p>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
               {/* Placeholder for when media is missing */}
              <p>Media not available.</p>
            </div>
          )}
        </div>

        {/* Text Content Section */}
        <div className={textboxClassName}>
          <div className='grid grid-cols-1 gap-xs'>
            {title && <h4 className='h3'>{title}</h4>}
            {content && <p className='h6 lg:body-16'>{content}</p>}
          </div>
          {link && ctaLabel && (
            <a href={link} className={`btn w-fit`} variant={ctaStyleHandle}>
              {ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionIngridItem; 